import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import type { Equipment, UsageSession } from '../../types/equipment';
import { Clock, MapPin, Gauge, Wrench, ChevronRight, History } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useOrgTimezone, formatDateInTz } from '@/utils/timezone';
import { toNum, toNumOr } from '@/utils/numeric';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(session: UsageSession): string {
  if (!session.started_at || !session.completed_at) return '—';
  const ms = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function meterDelta(session: UsageSession): string | null {
  if (!session.meter_type) return null;
  const parts: string[] = [];
  const hoursStart = toNum(session.hours_start);
  const hoursEnd = toNum(session.hours_end);
  const kmStart = toNum(session.km_start);
  const kmEnd = toNum(session.km_end);
  if ((session.meter_type === 'hours' || session.meter_type === 'both') &&
      hoursStart != null && hoursEnd != null) {
    const delta = hoursEnd - hoursStart;
    if (delta > 0) parts.push(`+${delta.toFixed(1)} hrs`);
  }
  if ((session.meter_type === 'km' || session.meter_type === 'both') &&
      kmStart != null && kmEnd != null) {
    const delta = kmEnd - kmStart;
    if (delta > 0) parts.push(`+${delta.toFixed(0)} km`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

const STATUS_COLOURS: Record<string, string> = {
  completed:  'bg-green-100 text-green-700',
  active:     'bg-blue-100 text-blue-700',
  pre_start:  'bg-neutral-100 text-neutral-600',
  cancelled:  'bg-red-100 text-red-600',
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function SessionHistory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const eqId = parseInt(id!);

  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [sessions, setSessions]   = useState<UsageSession[]>([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.getSessions(eqId),
    ]).then(([eqRes, sessRes]) => {
      if (eqRes.success  && eqRes.data)  setEquipment(eqRes.data);
      if (sessRes.success && sessRes.data) setSessions(sessRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Usage History" />
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  // Aggregate totals
  const completed = sessions.filter(s => s.status === 'completed');
  const totalHours = completed.reduce((acc, s) => {
    const start = toNum(s.hours_start), end = toNum(s.hours_end);
    if (start != null && end != null) return acc + (end - start);
    return acc;
  }, 0);
  const totalKm = completed.reduce((acc, s) => {
    const start = toNum(s.km_start), end = toNum(s.km_end);
    if (start != null && end != null) return acc + (end - start);
    return acc;
  }, 0);
  const totalGpsKm = completed.reduce((acc, s) => acc + toNumOr(s.total_distance_km), 0);

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Usage History"
        subtitle={equipment?.name ?? ''}
      />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Summary stats */}
        {completed.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-neutral-100 rounded-xl px-3 py-3 text-center">
              <p className="text-lg font-bold text-neutral-800">{completed.length}</p>
              <p className="text-xs text-neutral-400">Sessions</p>
            </div>
            {totalHours > 0 && (
              <div className="bg-white border border-neutral-100 rounded-xl px-3 py-3 text-center">
                <p className="text-lg font-bold text-neutral-800">{totalHours.toFixed(1)}</p>
                <p className="text-xs text-neutral-400">Hours logged</p>
              </div>
            )}
            {totalKm > 0 && (
              <div className="bg-white border border-neutral-100 rounded-xl px-3 py-3 text-center">
                <p className="text-lg font-bold text-neutral-800">{totalKm.toFixed(0)}</p>
                <p className="text-xs text-neutral-400">km logged</p>
              </div>
            )}
            {totalGpsKm > 0 && totalHours === 0 && (
              <div className="bg-white border border-neutral-100 rounded-xl px-3 py-3 text-center">
                <p className="text-lg font-bold text-neutral-800">{totalGpsKm.toFixed(1)}</p>
                <p className="text-xs text-neutral-400">GPS km</p>
              </div>
            )}
          </div>
        )}

        {/* Session list */}
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-10 w-10 text-neutral-200 mx-auto mb-3" />
            <p className="text-neutral-500">No sessions recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => {
              const delta = meterDelta(s);
              const operatorName = [s.operator_first_name, s.operator_last_name].filter(Boolean).join(' ');
              return (
                <button
                  key={s.id}
                  onClick={() => s.status === 'active' ? navigate(`/equipment/sessions/${s.id}`) : undefined}
                  className={cn(
                    'w-full bg-white border border-neutral-100 rounded-xl px-4 py-3 text-left',
                    s.status === 'active' && 'hover:shadow-md transition-all cursor-pointer'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium capitalize', STATUS_COLOURS[s.status] ?? 'bg-neutral-100 text-neutral-600')}>
                          {s.status.replace(/_/g, ' ')}
                        </span>
                        {s.started_at && (
                          <span className="text-xs text-neutral-400">
                            {formatDateInTz(s.started_at, orgTz)}
                          </span>
                        )}
                      </div>

                      {s.task_description && (
                        <p className="text-sm font-medium text-neutral-800 truncate">{s.task_description}</p>
                      )}

                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        {/* Elapsed and metered are different numbers whenever
                            the work was paused, so neither is left unlabelled —
                            side by side and bare they read as a contradiction. */}
                        {s.status === 'completed' && (
                          <span className="flex items-center gap-1 text-xs text-neutral-500">
                            <Clock className="h-3 w-3" />
                            {formatDuration(s)} elapsed
                          </span>
                        )}
                        {delta && (
                          <span className="flex items-center gap-1 text-xs text-neutral-500">
                            <Gauge className="h-3 w-3" />
                            {delta} on the meter
                          </span>
                        )}
                        {toNumOr(s.total_distance_km) > 0 && (
                          <span className="flex items-center gap-1 text-xs text-neutral-500">
                            <MapPin className="h-3 w-3" />
                            {toNumOr(s.total_distance_km).toFixed(2)} km GPS
                          </span>
                        )}
                        {s.farm_name && (
                          <span className="text-xs text-neutral-400">{s.farm_name}</span>
                        )}
                        {operatorName && (
                          <span className="text-xs text-neutral-400">by {operatorName}</span>
                        )}
                      </div>

                      {/* Implements */}
                      {s.implements && s.implements.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {s.implements.map(impl => (
                            <span key={impl.id} className="flex items-center gap-1 text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">
                              <Wrench className="h-2.5 w-2.5" />
                              {impl.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {s.status === 'active' && (
                      <ChevronRight className="h-4 w-4 text-neutral-300 flex-shrink-0 mt-1" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <Button variant="outline" className="w-full" onClick={() => navigate(`/equipment/${eqId}`)}>
          Back to {equipment?.name ?? 'Equipment'}
        </Button>

      </div>
    </div>
  );
}
